import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { Card } from '@/components/ui/card';
import { PendingApproval } from '@/components/PendingApproval';
import { TerminatedAccount } from '@/components/TerminatedAccount';
import { isExtendedUser } from '@/types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Optional replacement for the default auth-loading screen so a route can
   * match its destination's loading visuals (e.g. Artemis).
   */
  loadingFallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, loadingFallback }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // NO MORE DATABASE QUERY! Use data from AuthContext directly
  // The user object already has employment_status from loadCompleteUserProfile
  const userStatus = isExtendedUser(user) ? user.employment_status : null;

  if (loading) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="w-96 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </Card>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check if user employment status is pending or terminated
  // employment_status is the single source of truth
  const isPendingUser = userStatus === 'pending';
  const isTerminatedUser = userStatus === 'terminated';

  if (isPendingUser) {
    // Show pending approval screen
    return <PendingApproval />;
  }

  if (isTerminatedUser) {
    // Show terminated account screen
    return <TerminatedAccount />;
  }

  return <>{children}</>;
};